import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom';
import { Button, Col, Container, Input, Label, Row, Spinner, Table } from 'reactstrap';
import AdminFunctions from './AdminFunctions';
import AuthFunctions from '../services/AuthService';
import Session from '../auth/Session'

const Audit = () => {
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const [auditLog, setAuditLog] = useState([]);

    const navigate = useNavigate();

    useEffect(() => {
        async function isAuth() {
            var isAuthenticated = await AuthFunctions.getAuthenticatedAsync();
            if (!isAuthenticated) {
                navigate('/login');
            }
        }
        async function fetchIsAdmin() {
            const isAdmin = await AdminFunctions.isAdminAsync();
            if (!isAdmin) {
                navigate('/accessdenied');
                return;
            }
        }

        isAuth();
        fetchIsAdmin();
    }, [navigate]);

    useEffect(() => {
        loadAuditLogs();
    }, []);

    const loadAuditLogs = async () => {
        try{
            setError("");
            setLoading(true);
            const data = await getAuditLogs();
            setAuditLog(data || []);
        } catch (e) {
            setError(e?.message || "Failed to load audit logs.")
        } finally {
            setLoading(false);
        }
    }


    const getAuditLogs = async () => {
        var token = Session.getToken();
        const response = await fetch(`api/Admin/GetAllAuditLogs`, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        });

        if(!response.ok){
            const text = await response.text();
            throw new Error(text || "Failed to load audit logs.");
        }

        return await response.json();
    }

    const filteredAuditLog = useMemo(() => {
        const term = search.trim().toLowerCase();

        return auditLog.filter((e) => {
            const matchesSearch =
                !term ||
                (e.user || '').toLowerCase().includes(term) ||
                (e.ipAddress || '').toLowerCase().includes(term) ||
                (e.object || '').toLowerCase().includes(term) ||
                (e.objectId || '').toLowerCase().includes(term) ||
                (e.changeType || '').toLowerCase().includes(term) ||
                (e.field || '').toLowerCase().includes(term) ||
                (e.beforeChange || '').toLowerCase().includes(term) ||
                (e.afterChange || '').toLowerCase().includes(term);

            if (!e.date) return matchesSearch;

            // just take the date portion so we can compare with the start/end date inputs
            const entryDateStr = e.date.split('T')[0];

            const matchesStart = !startDate || entryDateStr >= startDate;
            const matchesEnd = !endDate || entryDateStr <= endDate;

            return matchesSearch && matchesStart && matchesEnd;
        });
    }, [auditLog, search, startDate, endDate]);

    return (
        <>
            {error ? (
                <Row>
                    <Col>
                        <div className="alert alert-danger mb-0">{error}</div>
                    </Col>
                </Row>
            ) : null}
            
            <Container className="container-main">
                {/* Header */}
                <Row className="align-items-center g-3">
                    <Col>
                        <h3>Audit</h3>
                    </Col>
                </Row>

                {loading ? (
                <div
                    style={{
                    marginTop: "2rem",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    }}
                >
                    <Spinner />
                </div>
                ) : (
                <>
                    {/* Search */}
                    <Row className="row-20 align-items-end" style={{ marginTop: 10 }}>
                        <Col md="2">
                            <Label>Start Date</Label>
                            <Input 
                                type='date'
                                placeholder="Start Date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </Col>
                        <Col md="2">
                            <Label>End Date</Label>
                            <Input 
                                type='date'
                                placeholder="End Date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </Col>
                        <Col md="6">
                            <Input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search..."
                            />
                        </Col>
        
                        <Col md="2" className="d-flex justify-content-md-end align-items-center">
                            <div className="text-muted" style={{ fontSize: 14 }}>
                            Showing {filteredAuditLog.length} of {auditLog.length}
                            </div>
                        </Col>
                    </Row>

                    {/* Audit table */}
                    <Row className="row-20">
                        <Col>
                            <Table bordered hover responsive>
                                <thead>
                                    <tr>
                                        <th>Date/Time</th>
                                        <th>User</th>
                                        <th>IP Address</th>
                                        <th>Object</th>
                                        <th>Object ID</th>
                                        <th>Change Type</th>
                                        <th>Field</th>
                                        <th>Before Change</th>
                                        <th>After Change</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredAuditLog?.map((row) => (
                                    <tr
                                        key={row.id}
                                    >
                                        <td>{new Date(row.date).toLocaleString()}</td>
                                        <td>{row.user}</td>
                                        <td>{row.ipAddress}</td>
                                        <td>{row.object === "" ? "—" : row.object}</td>
                                        <td>{row.objectId === "" ? "—" : row.objectId}</td>
                                        <td>{row.changeType === "" ? "—" : row.changeType}</td>
                                        <td>{row.field === "" ? "—" : row.field}</td>
                                        <td>{row.beforeChange === "" ? "—" : row.beforeChange}</td>
                                        <td>{row.afterChange === "" ? "—" : row.afterChange}</td>
                                    </tr>
                                    ))}
                                    {filteredAuditLog?.length === 0 && (
                                    <tr>
                                        <td colSpan={9} style={{ textAlign: "center" }}>
                                        No logs found.
                                        </td>
                                    </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Col>
                    </Row>
                </>
                )}
            </Container>
        </>
    );
}

export default Audit